import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from './atoms/Input';
import Button from './atoms/Button';
import Switch from './atoms/Switch';
import { ScannerTypeDTO } from '../models/scanner-type';
import {
  useCreateScannerTypeMutation,
  useUpdateScannerTypeMutation,
} from '../store';
import { toastSuccess } from '../utils/toasts';

interface Props {
  businessId: string;
  eventId: string;
  scannerType?: ScannerTypeDTO;
  onSuccess: () => void;
  onClose: () => void;
}

interface FormData {
  name: string;
  description: string;
  is_active: boolean;
}

const validationSchema = yup.object<FormData>().shape({
  name: yup.string().required('Debes ingresar un nombre'),
  description: yup.string().optional().default(''),
  is_active: yup.boolean().required().default(true),
});

const CreateEditScannerType: React.FC<Props> = ({
  businessId, eventId, scannerType, onSuccess, onClose,
}) => {
  const {
    control, handleSubmit, formState, watch, setValue,
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: scannerType?.name || '',
      description: scannerType?.description || '',
      is_active: scannerType?.is_active ?? true,
    },
  });

  const [createScannerType, createResult] = useCreateScannerTypeMutation();
  const [updateScannerType, updateResult] = useUpdateScannerTypeMutation();

  const onSubmit = async (data: FormData) => {
    try {
      let res;
      if (scannerType) {
        res = await updateScannerType({
          businessId,
          eventId,
          data: {
            id: scannerType.id,
            name: data.name,
            description: data.description || undefined,
            is_active: data.is_active,
          },
        });
      } else {
        res = await createScannerType({
          businessId,
          eventId,
          data: {
            name: data.name,
            description: data.description || undefined,
            is_active: data.is_active,
          },
        });
      }
      if (!('error' in res)) {
        toastSuccess(scannerType ? 'Tipo de escáner actualizado' : 'Tipo de escáner creado');
        onSuccess();
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <form className="w-full h-full flex flex-col gap-3" onSubmit={handleSubmit(onSubmit)}>
      <Switch
        label="Activo"
        labelClassName="font-medium"
        checked={watch('is_active')}
        onChange={(e) => setValue('is_active', e.target.checked)}
      />
      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <Input
            label="Nombre *"
            labelAbove
            id="name"
            placeholder="Nombre del tipo de escáner"
            {...field}
            errMsg={formState.errors.name?.message}
          />
        )}
      />
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <Input
            label="Descripción (opcional)"
            labelAbove
            id="description"
            placeholder="Descripción del tipo de escáner"
            {...field}
            errMsg={formState.errors.description?.message}
          />
        )}
      />
      <div className="flex mt-4 gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          loading={createResult.isLoading || updateResult.isLoading}
        >
          Guardar
        </Button>
      </div>
    </form>
  );
};

export default CreateEditScannerType;
